import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    Typography,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
    Checkbox
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { FinanceService } from '../../../api/services/financeService';
import { SettingsService } from '../../../api/services/settingsService';
import { toast } from '../../../utils/toast';
import { useCurrency, formatCurrency } from '../../../utils/currency';
import { useSelector } from 'react-redux';
import { isUaeProperty } from '../../../utils/dubaiHospitalityTax';

export const calculateFolioRoomCharge = (targetFolio) => {
    if (!targetFolio) return 0;
    const folioObj = targetFolio?.data || targetFolio?.primaryFolio || (Array.isArray(targetFolio?.folios) ? targetFolio.folios[0] : targetFolio);
    const items = folioObj?.items || folioObj?.transactions || (Array.isArray(folioObj) ? folioObj : []);
    if (Array.isArray(items) && items.length > 0) {
        return items
            .filter(item => !item?.isVoided && (item?.category === 'Room Charge' || String(item?.category || '').trim().toLowerCase() === 'room charge'))
            .reduce((sum, item) => sum + (parseFloat(item?.amount) || 0), 0);
    }
    if (folioObj?.roomCharge !== undefined && folioObj?.roomCharge !== null) {
        return Number(folioObj.roomCharge) || 0;
    }
    return 0;
};

export default function AddFolioPaymentModal({
    open,
    onClose,
    folio,
    folioId,
    folioBalance,
    onSuccess,
    isUae: isUaeProp,
    roomCharge: roomChargeProp,
    items: itemsProp
}) {
    const { currency, currencySymbol } = useCurrency();
    const { tenants, user } = useSelector((state) => state.auth || {});
    const activeTenantId = user?.tenantId;
    const tenant = tenants?.find(t => String(t.tenantId) === String(activeTenantId) || String(t.id) === String(activeTenantId) || String(t._id) === String(activeTenantId));
    const isUae = isUaeProp !== undefined
        ? Boolean(isUaeProp)
        : (currency === 'AED' || (tenant?.currency === 'AED') || isUaeProperty(tenant) || (user?.hotel && isUaeProperty(user.hotel)));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const [internalFolio, setInternalFolio] = useState(null);
    const [finalCheckoutSettlement, setFinalCheckoutSettlement] = useState(false);
    const [form, setForm] = useState({
        amount: '',
        type: 'Deposit', // Deposit, Settlement, Adjust
        paymentMethod: '',
        referenceNumber: '',
        description: '',
        date: '',
        finalDiscount: ''
    });

    useEffect(() => {
        if (!open) {
            setInternalFolio(null);
            setFinalCheckoutSettlement(false);
            return;
        }

        if (folio && (folio.items || folio.data?.items)) {
            setInternalFolio(folio);
        } else if (folioId) {
            FinanceService.getGuestFolioLedger(folioId)
                .then(res => {
                    const loaded = res?.primaryFolio || (Array.isArray(res?.folios) ? res.folios[0] : null) || res?.rawReservation || res;
                    if (loaded) {
                        setInternalFolio(loaded);
                    }
                })
                .catch(err => {
                    console.error('Failed to load folio in AddFolioPaymentModal:', err);
                });
        }
    }, [open, folio, folioId]);

    const activeFolio = folio || internalFolio;
    const resolvedRoomCharge = roomChargeProp !== undefined
        ? Number(roomChargeProp) || 0
        : calculateFolioRoomCharge(itemsProp ? { items: itemsProp } : activeFolio);

    const fetchPaymentMethods = async () => {
        setLoadingMethods(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const resolvedTenantId = activeTenantId || authData.tenantId || null;
            const res = await SettingsService.getPaymentMethods(resolvedTenantId, { noPagination: true });
            const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
            const activeList = list.filter(m => m.enabled === undefined || m.enabled === true || m.status === 'Active');
            setPaymentMethods(activeList);
        } catch (err) {
            console.error('Failed to load payment methods from API:', err);
            setPaymentMethods([]);
        } finally {
            setLoadingMethods(false);
        }
    };

    useEffect(() => {
        if (open) {
            const now = new Date();
            const localISOTime = (new Date(now.getTime() - now.getTimezoneOffset() * 60000)).toISOString().slice(0,10);

            setFinalCheckoutSettlement(false);
            setForm({
                amount: folioBalance !== undefined && folioBalance > 0 ? Number(folioBalance).toFixed(2) : '',
                type: 'Deposit', // Default to Deposit as it's first in the user story
                paymentMethod: '',
                referenceNumber: '',
                description: '',
                date: localISOTime,
                finalDiscount: ''
            });

            fetchPaymentMethods();
        }
    }, [open, folioBalance]);

    const handleTextChange = (field) => (event) => {
        setForm(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleAmountChange = (e) => {
        let val = e.target.value;
        // If the user types when the field contains '0.00', strip the leading '0.00'
        if (val.startsWith('0.00') && val.length > 4 && /^\d+$/.test(val.slice(4))) {
            val = val.slice(4);
        } else if (val === '0.00') {
            val = '';
        }
        if (!val || val.match(/^\d*\.?\d*$/)) {
            setForm(prev => ({ ...prev, amount: val }));
        }
    };

    const handleFinalCheckoutSettlementToggle = (checked) => {
        setFinalCheckoutSettlement(checked);
        if (checked) {
            setForm(prev => {
                const total = (folioBalance !== undefined && folioBalance !== null && !isNaN(Number(folioBalance)))
                    ? Math.abs(Number(folioBalance))
                    : (parseFloat(prev.amount) || 0);
                const discountNum = parseFloat(prev.finalDiscount) || 0;
                const net = Math.max(0, total - discountNum);
                return {
                    ...prev,
                    type: 'Settlement',
                    amount: net > 0 ? net.toFixed(2) : prev.amount
                };
            });
        } else {
            setForm(prev => {
                const total = (folioBalance !== undefined && folioBalance !== null && !isNaN(Number(folioBalance)))
                    ? Math.abs(Number(folioBalance))
                    : ((parseFloat(prev.amount) || 0) + (parseFloat(prev.finalDiscount) || 0));
                return {
                    ...prev,
                    finalDiscount: '',
                    amount: total > 0 ? total.toFixed(2) : prev.amount
                };
            });
        }
    };

    const handleDiscountChange = (e) => {
        const val = e.target.value;
        if (!val || val.match(/^\d*\.?\d*$/)) {
            const discountNum = parseFloat(val) || 0;
            const total = (folioBalance !== undefined && folioBalance !== null && !isNaN(Number(folioBalance)))
                ? Math.abs(Number(folioBalance))
                : ((parseFloat(form.amount) || 0) + (parseFloat(form.finalDiscount) || 0));
            const netPayable = Math.max(0, total - discountNum);
            setForm(prev => ({
                ...prev,
                finalDiscount: val,
                amount: netPayable.toFixed(2)
            }));
        }
    };

    const handleSubmit = async () => {
        const amountVal = parseFloat(form.amount);
        if (!form.amount || isNaN(amountVal) || amountVal <= 0) {
            toast.error('Payment amount is required and must be greater than zero.');
            return;
        }

        if (!form.paymentMethod) {
            toast.error('Payment method is required. Please select a payment method.');
            return;
        }

        setIsSubmitting(true);
        try {
            // Pass 'Settlement', 'Deposit', or 'Adjust' directly
            const backendType = form.type;

            const payload = {
                amount: amountVal,
                paymentMethod: form.paymentMethod || (amountVal === 0 ? 'Other' : ''), // 'Cash', 'Card', 'UPI', 'Bank Transfer', or 'Other'
                type: backendType, // 'Deposit', 'Settlement', or 'Adjust'
                referenceNumber: (form.referenceNumber || '').trim(),
                description: (form.description || '').trim(),
                date: form.date ? new Date(form.date).toISOString() : new Date().toISOString()
            };

            if (form.type === 'Settlement' && finalCheckoutSettlement && form.finalDiscount) {
                const discountVal = parseFloat(form.finalDiscount);
                if (!isNaN(discountVal) && discountVal > 0) {
                    payload.finalDiscount = discountVal;
                }
            }

            await FinanceService.addFolioPayment(folioId, payload);
            toast.success('Payment successfully posted; guest folio balance updated');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to post folio payment:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to process payment';
            toast.error(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentDiscount = parseFloat(form.finalDiscount) || 0;
    const currentBaseTotal = (folioBalance !== undefined && folioBalance !== null && !isNaN(Number(folioBalance)))
        ? Math.abs(Number(folioBalance))
        : ((parseFloat(form.amount) || 0) + currentDiscount);
    const discountedTotalAmount = Math.max(0, currentBaseTotal - currentDiscount);

    return (
        <Dialog open={open} onClose={!isSubmitting ? onClose : undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' } }}>
            <Box sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', p: 3, color: 'white', display: 'flex', alignItems: 'center' }}>
                <PaymentsIcon sx={{ mr: 1.5, fontSize: 28 }} />
                <Typography variant="h5" fontWeight="700">Collect Payment</Typography>
            </Box>
            <DialogContent sx={{ p: 4 }}>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Record a guest payment directly against their active reservation ledger balance.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Transaction Type</Typography>
                        <ToggleButtonGroup
                            color="primary"
                            value={form.type}
                            exclusive
                            onChange={(e, newType) => {
                                if (newType !== null) {
                                    setForm(prev => {
                                        let nextAmount = prev.amount;
                                        const total = (folioBalance !== undefined && folioBalance !== null && !isNaN(Number(folioBalance)))
                                            ? Math.abs(Number(folioBalance))
                                            : (parseFloat(prev.amount) || 0);
                                        if (newType === 'Settlement') {
                                            const discountNum = finalCheckoutSettlement ? (parseFloat(prev.finalDiscount) || 0) : 0;
                                            const net = Math.max(0, total - discountNum);
                                            nextAmount = net > 0 ? net.toFixed(2) : '';
                                        } else {
                                            nextAmount = total > 0 ? total.toFixed(2) : '';
                                        }
                                        return {
                                            ...prev,
                                            type: newType,
                                            amount: nextAmount,
                                            finalDiscount: newType !== 'Settlement' ? '' : prev.finalDiscount
                                        };
                                    });
                                    if (newType !== 'Settlement') {
                                        setFinalCheckoutSettlement(false);
                                    }
                                }
                            }}
                            fullWidth
                            size="small"
                        >
                            <ToggleButton value="Deposit">Deposit / Advance</ToggleButton>
                            <ToggleButton value="Adjust">Adjust</ToggleButton>
                            <ToggleButton value="Settlement">Settlement</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Final Checkout Settlement Option (Only visible in Settlement section when Room Charge > 0) */}
                    {form.type === 'Settlement' && resolvedRoomCharge > 0 && (
                        <Box
                            onClick={() => handleFinalCheckoutSettlementToggle(!finalCheckoutSettlement)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.75,
                                mb: finalCheckoutSettlement ? 2 : 3,
                                borderRadius: 2.5,
                                bgcolor: finalCheckoutSettlement ? '#f0fdf4' : '#ffffff',
                                border: '1px solid',
                                borderColor: finalCheckoutSettlement ? '#10b981' : '#e2e8f0',
                                boxShadow: finalCheckoutSettlement ? '0 2px 8px rgba(16, 185, 129, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.04)',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: finalCheckoutSettlement ? '#059669' : '#cbd5e1',
                                    bgcolor: finalCheckoutSettlement ? '#ecfdf5' : '#f8fafc'
                                }
                            }}
                        >
                            <Checkbox
                                checked={finalCheckoutSettlement}
                                onChange={(e) => handleFinalCheckoutSettlementToggle(e.target.checked)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    p: 0,
                                    color: '#94a3b8',
                                    '&.Mui-checked': {
                                        color: '#10b981'
                                    }
                                }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.25 }}>
                                    Final Checkout Settlement
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem', display: 'block', mt: 0.25 }}>
                                    Apply a flat checkout discount on Room & Tax charges
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Discount Field (Only visible in Settlement section when enabled) */}
                    {form.type === 'Settlement' && resolvedRoomCharge > 0 && finalCheckoutSettlement && (
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Final Discount Amount"
                                placeholder="0.00"
                                value={form.finalDiscount}
                                onChange={handleDiscountChange}
                                disabled={isSubmitting}
                                helperText={`Applies to Room & Tax charges (Max: ${formatCurrency(resolvedRoomCharge, currency)})`}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                                }}
                            />

                            <Box sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
                                    <Typography variant="body2" fontWeight="600">{formatCurrency(currentBaseTotal, currency)}</Typography>
                                </Box>
                                {currentDiscount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">Discount Amount:</Typography>
                                        <Typography variant="body2" fontWeight="600" color="success.main">-{formatCurrency(currentDiscount, currency)}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px dashed #cbd5e1' }}>
                                    <Typography variant="subtitle2" fontWeight="700" color="text.primary">Discounted Total Amount:</Typography>
                                    <Typography variant="subtitle2" fontWeight="800" color="success.main">
                                        {formatCurrency(discountedTotalAmount, currency)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <TextField
                        fullWidth
                        required
                        label={form.type === 'Settlement' && finalCheckoutSettlement && currentDiscount > 0 ? 'Payment Amount (Net Payable)' : 'Payment Amount'}
                        placeholder="0.00"
                        value={form.amount}
                        onChange={handleAmountChange}
                        onFocus={(e) => {
                            if (form.amount === '0.00' || form.amount === '0') {
                                setForm(prev => ({ ...prev, amount: '' }));
                            } else {
                                e.target.select();
                            }
                        }}
                        disabled={isSubmitting}
                        helperText={form.type === 'Settlement' && finalCheckoutSettlement && currentDiscount > 0 ? `Auto-adjusted after ${formatCurrency(currentDiscount, currency)} discount` : undefined}
                        sx={{ mb: 3 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                        }}
                    />

                    <FormControl fullWidth sx={{ mb: 3 }} required>
                        <InputLabel required>Payment Method / Tender</InputLabel>
                        <Select
                            value={form.paymentMethod}
                            label="Payment Method / Tender *"
                            onChange={handleTextChange('paymentMethod')}
                            disabled={isSubmitting || loadingMethods}
                        >
                            {loadingMethods ? (
                                <MenuItem disabled value="">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2">Loading payment methods...</Typography>
                                    </Box>
                                </MenuItem>
                            ) : paymentMethods.length > 0 ? (
                                paymentMethods.map((method, idx) => {
                                    const val = typeof method === 'string' ? method : (method.name || method.code || method.id);
                                    const label = typeof method === 'string' ? method : (method.label || method.name || method.code || val);
                                    return (
                                        <MenuItem key={method.id || method._id || val || idx} value={val}>
                                            {label}
                                        </MenuItem>
                                    );
                                })
                            ) : (
                                <MenuItem disabled value="">No payment methods available</MenuItem>
                            )}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        required
                        label="Payment Date"
                        type="date"
                        value={form.date}
                        onChange={handleTextChange('date')}
                        disabled={isSubmitting}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        fullWidth
                        label="Transaction Reference Number"
                        placeholder="e.g., txn_98765, Receipt ID, Check #"
                        value={form.referenceNumber}
                        onChange={handleTextChange('referenceNumber')}
                        disabled={isSubmitting}
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        fullWidth
                        label="Reason / Description"
                        placeholder="e.g., Partial payment for room, Deposit for spa..."
                        value={form.description}
                        onChange={handleTextChange('description')}
                        disabled={isSubmitting}
                        multiline
                        rows={2}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <Button
                    onClick={onClose}
                    disabled={isSubmitting}
                    sx={{ color: '#64748b', fontWeight: 600, px: 2 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{
                        px: 4, py: 1.5, borderRadius: '8px', fontWeight: 700,
                        boxShadow: '0 4px 14px rgba(var(--primary-color-rgb, 65, 70, 224), 0.25)',
                        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(var(--primary-color-rgb, 65, 70, 224), 0.35)' },
                        transition: 'all 0.2s'
                    }}
                >
                    {isSubmitting ? 'Processing...' : 'Post Payment'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
